import React, { useCallback, useRef, useState } from "react";
import {
  Button,
  ConfigProvider,
  Form,
  Progress,
  Radio,
  Select,
  Tooltip,
  Typography,
} from "antd";
import { message } from "antd";
import enUS from "antd/locale/en_US";
import classNames from "classnames";

import classes from "./Run.module.scss";
import FormSection from "components/form-section";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getCOProjects,
  getRunProgress,
  runCOProject,
} from "service/completionOptimizer";

const OptimizerRunPage = () => {
  const [progress, setProgress] = useState(null);
  const [form] = Form.useForm();
  const progressPollingInterval = useRef(null);

  const { data: projects } = useQuery({
    queryKey: ["co_getProjectList"],
    queryFn: () => getCOProjects(),
  });

  const { mutate: run, isPending } = useMutation({
    mutationFn: (data) => runCOProject({ data }),
    onSettled: () => {
      // Remove this part - don't clear progress on settle
      // if (progressPollingInterval.current) clearInterval(progressPollingInterval.current)
      // setProgress(null)
    },
  });

  const onSubmit = useCallback(
    (data) => {
      // Clear previous progress when starting a new run
      setProgress(null);
      if (progressPollingInterval.current)
        clearInterval(progressPollingInterval.current);
      if (data.project && data.model && data.hardware && data.op_frequency) {
        run(data);
      } else {
        console.log("Invalid form data");
        message.error("Invalid form data");
        return;
      }

      // run(data)
      // if (progressPollingInterval.current) clearInterval(progressPollingInterval.current)

      const checkProgress = async () => {
        const progressRes = await getRunProgress(
          data.project,
          data.model
        ).catch(() => {});
        if (!progressRes) return;
        setProgress(progressRes.progress);

        // Stop polling when reaching 100%
        if (progressRes.progress === 100) {
          clearInterval(progressPollingInterval.current);
        }
      };
      try {
        checkProgress();
        progressPollingInterval.current = setInterval(checkProgress, 5000);
      } catch (error) {
        console.log(error);
        if (progressPollingInterval.current)
          clearInterval(progressPollingInterval.current);
      }
    },
    [run]
  );

  const getProgressStatus = (progress) => {
    if (progress === 100) return "success";
    if (progress === null) return "normal";
    return "active";
  };

  const getProgressText = (progress) => {
    if (progress === null) return "Waiting to start...";
    if (progress === 100) return "Completed!";
    return "Processing...";
  };

  return (
    <ConfigProvider
      locale={enUS}
      componentSize="middle"
      theme={{
        token: {},
        components: {},
      }}
    >
      <Form
        form={form}
        className={classNames(
          "px-4 py-2 overflow-y-scroll h-100",
          classes.form
        )}
        layout="vertical"
        labelAlign="left"
        onFinish={onSubmit}
      >
        <FormSection title="Run">
          <div className="vstack gap-4">
            <Form.Item name={["project"]} label="Project">
              <Select
                options={projects?.map((p) => ({
                  value: p.pk,
                  label: p.general?.title,
                }))}
              />
            </Form.Item>
            <FormSection title="Computation Environment">
              <div className="flex items-center gap-20">
                <Form.Item name={["hardware"]} label="Hardware">
                  <Radio.Group>
                    <Radio value="CPU">CPU</Radio>
                    <Radio value="GPU">GPU</Radio>
                  </Radio.Group>
                </Form.Item>
                <Form.Item name={["model"]} label="Solver">
                  <Radio.Group>
                    <Radio value="lite">Lite-EarthMind</Radio>
                    <Radio value="pure">EarthMind</Radio>
                  </Radio.Group>
                </Form.Item>
              </div>
            </FormSection>
            <FormSection title="I/O Control">
              <Form.Item name={["op_frequency"]} label="Output Frequency">
                <Radio.Group>
                  <Radio value="DAY">Day</Radio>
                  <Radio value="MONTH">Month</Radio>
                  <Radio value="YEAR">Year</Radio>
                </Radio.Group>
              </Form.Item>
            </FormSection>
            <div className="vstack align-items-center gap-2">
              {progress !== null && (
                <>
                  <Typography.Text style={{ fontSize: "24px", color: "white" }}>
                    {getProgressText(progress)}
                  </Typography.Text>
                  <Tooltip title={`${progress}% Complete`} placement="top">
                    <div style={{ width: "100%", maxWidth: 1200 }}>
                      <Progress
                        percent={progress}
                        status={getProgressStatus(progress)}
                        trailColor="#fefefe"
                        strokeColor={{
                          "0%": "#108ee9",
                          "100%": "#87d068",
                        }}
                        strokeWidth={20}
                        format={(percent) => (
                          <span style={{ color: "white", fontSize: "18px" }}>
                            {`${percent}%`}
                          </span>
                        )}
                      />
                    </div>
                  </Tooltip>
                </>
              )}
              <Button htmlType="submit" loading={isPending}>
                Run
              </Button>
            </div>
          </div>
        </FormSection>
      </Form>
    </ConfigProvider>
  );
};

export default OptimizerRunPage;
